import React, { useEffect, useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import Button from "@amzn/meridian/button";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { createWorkflowInspection, getScheduledWorkflow } from "src/store/workflowSlice";
import { resetInspectionCaptureState } from "src/store/inspectionCaptureSlice";
import Loader from "@amzn/meridian/loader";
import DateRangePicker from "@amzn/meridian/date-range-picker";
import { dateMonthsAgo, toDateInputValue, toStartOfDayIso, toEndOfDayIso } from "src/utils/dateUtil";
import inspectionService, { InspectionData, InspectionListView } from "src/services/inspectionService";
import { InspectionQuestion } from "./types/inspectionWorkflow";
import ThemedTag from "../configurationDrivenInspections/inspectionReview/ThemedTag";
import ErrorSheet from "src/components/UI/ErrorSheet";
import DraftInspectionSheet from "./DraftInspectionSheet";
import HistoryInspectionCard from "./HistoryInspectionCard";
import { computeResumePosition } from "./utils/resumePosition";
import { inspectionFlowTimingLogger } from "src/services/inspectionFlowTimingLogger";
import "./austinInspectionCheckpointsPage.scss";
/** A single stage in the inspection timeline. */
type PhaseMode = "AI" | "MANUAL";

interface InspectionPhase {
  title: string;
  /** Omitted for the highlighted summary-style phase (e.g. Review & Submit). */
  mode?: PhaseMode;
  description: string;
  /** Secondary meta line, e.g. "8 Questions.". */
  meta?: string;
  /** Renders the card as a filled green highlight card. */
  highlighted?: boolean;
}

/**
 * Shape of the details a DUE Austin inspection card passes through router
 * state. Everything is optional so the page still renders sensible defaults
 * when opened directly.
 */
export interface AustinInspectionDetailsState {
  siteCode?: string;
  austinInspectionId?: string;
  name?: string;
  frequency?: string;
}

const DEFAULT_NAME = "Central Waste Accumulation Area Inspection";
const DEFAULT_FREQUENCY = "SEMI WEEKLY";

/** How many months of history the range defaults to (last 6 months → today). */
const DEFAULT_HISTORY_MONTHS = 6;

/** [start, end] calendar dates (YYYY-MM-DD) for the default history window. */
const defaultHistoryRange = (): [string, string] => [
  dateMonthsAgo(DEFAULT_HISTORY_MONTHS),
  toDateInputValue(new Date()),
];

/** Formats a phase count into its meta label (always shows the card). */
const formatQuestionMeta = (count: number): string =>
  `${count} ${count === 1 ? "Question" : "Questions"}.`;

/**
 * Builds the timeline phases from the fetched inspection questions.
 *
 * Counts are derived directly from the workflow's question list:
 * - Cage Walk    → mode === "ai" && iterable === true
 * - Area Scan    → mode === "ai" && iterable === false
 * - Photo Evidence → mode === "image"
 * - Manual Checks  → mode === "manual"
 *
 * Cards are always rendered, even when a count is 0.
 */
const buildPhases = (questions: InspectionQuestion[]): InspectionPhase[] => {
  const cageWalkCount = questions.filter(
    (q) => q.mode === "ai" && q.iterable
  ).length;
  const areaScanCount = questions.filter(
    (q) => q.mode === "ai" && !q.iterable
  ).length;
  const photoEvidenceCount = questions.filter((q) => q.mode === "image").length;
  const manualChecksCount = questions.filter((q) => q.mode === "manual").length;

  return [
    {
      title: "Cage Walk",
      mode: "AI",
      description: "AI checks containers automatically as you scan each one.",
      meta: formatQuestionMeta(cageWalkCount),
    },
    {
      title: "Area Scan",
      mode: "AI",
      description:
        "Capture cage-level condition: signage, containment, emergency equipments",
      meta: formatQuestionMeta(areaScanCount),
    },
    {
      title: "Photo Evidence",
      mode: "MANUAL",
      description:
        "Capture cage-level condition: signage, containment, emergency equipments",
      meta: formatQuestionMeta(photoEvidenceCount),
    },
    {
      title: "Manual Checks",
      mode: "MANUAL",
      description: "Quick yes / no questions",
      meta: formatQuestionMeta(manualChecksCount),
    },
    {
      title: "Review & Submit",
      description:
        "Capture cage-level condition: signage, containment, emergency equipments",
      highlighted: true,
    },
  ];
};

type DetailsTab = "DETAILS" | "HISTORY";

const AustinInspectionCheckpointsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const state = (location.state ?? {}) as AustinInspectionDetailsState;

  const [activeTab, setActiveTab] = useState<DetailsTab>("DETAILS");
  const [startError, setStartError] = useState(false);
  // Disables START while the draft lookup is in flight (prevents double-trigger).
  const [checkingDraft, setCheckingDraft] = useState(false);
  const [draftInspection, setDraftInspection] = useState<InspectionData | null>(null);
  const [draftSheetOpen, setDraftSheetOpen] = useState(false);

  // Inspection History (HISTORY tab): completed inspections for this template at
  // this site within the selected date window, newest first. Fetched lazily the
  // first time the tab is opened and re-fetched whenever the date range changes.
  const [history, setHistory] = useState<InspectionListView[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  // [startDate, endDate] as YYYY-MM-DD; defaults to the last 6 months → today.
  const [dateRange, setDateRange] = useState<[string, string]>(defaultHistoryRange);

  // Fetch the config-driven workflow for this scheduled inspection. Extracted
  // so the error sheet's "Retry" action can re-run the same request.
  const fetchScheduledWorkflow = () => {
    if (state.austinInspectionId) {
      dispatch(getScheduledWorkflow(state.austinInspectionId));
    }
  };

  useEffect(() => {
    fetchScheduledWorkflow();
  }, [state.austinInspectionId]);

  // Read the fetched workflow and its questions from the store (populated by
  // the thunk above).
  const { activeWorkflow, questions, scheduledWorkflowLoading, scheduledWorkflowError } =
    useAppSelector((s) => s.workflow);

  const name = state.name ?? DEFAULT_NAME;
  const frequency = state.frequency ?? DEFAULT_FREQUENCY;
  // Total question count is the exact number of fetched workflow questions.
  const questionCount = questions.length;
  // Phases (and their per-phase counts) are derived from the fetched questions.
  // While questions are still loading this yields cards with 0 counts.
  const phases = buildPhases(questions);

  // History is keyed by templateName rather than templateId: for Austin
  // inspections, templateId is always the same shared value
  // ("WorkflowDriven-generated") regardless of which Austin inspection type is
  // being run, so it cannot distinguish e.g. "Containers and Labels" from
  // "Weekly Central Accumulation Area Checkpoint". templateName is the Austin
  // inspection's name (activeWorkflow.name) and is what actually varies per
  // type — the same field used as templateName when creating the inspection
  // in startNewInspection below. It's not passed through router state, so the
  // history fetch waits until the workflow resolves.
  const templateName = activeWorkflow?.name;

  const [startDate, endDate] = dateRange;

  // Fetch completed-inspection history for this template + site within the
  // selected window. The calendar gives YYYY-MM-DD; expand to full-day ISO
  // bounds so the backend's timestamp range is inclusive of the whole end day.
  // Extracted so the error state's "Try Again" can re-run the same request.
  const fetchHistory = async () => {
    if (!state.siteCode || !templateName || !startDate || !endDate) return;
    setHistoryLoading(true);
    setHistoryError(false);
    try {
      const result = await inspectionService.getInspectionHistory({
        siteCode: state.siteCode,
        templateName,
        startDate: toStartOfDayIso(startDate),
        endDate: toEndOfDayIso(endDate),
      });
      setHistory(result);
    } catch {
      setHistoryError(true);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch when the HISTORY tab is active and the workflow (templateName) and a
  // complete date range are available. Re-runs whenever the range changes so a
  // new selection refetches. templateName becomes defined once the workflow loads.
  useEffect(() => {
    if (activeTab !== "HISTORY") return;
    if (!state.siteCode || !templateName || !startDate || !endDate) return;
    fetchHistory();
  }, [activeTab, templateName, state.siteCode, startDate, endDate]);

  const handleHistoryCardClick = (inspection: InspectionListView) => {
    navigate(`/configurationDrivenInspections/${inspection.inspectionId}/review`);
  };

  // Create-and-start flow shared by the no-draft path and the draft sheet's
  // "clear & start new" action. On the SG side only: create and store the
  // inspection here, linking it to the Austin scheduled inspection so it can be
  // synced to RCMS once completed and submitted. We do not touch the RCMS
  // status at start time.
  const startNewInspection = async () => {
    if (!activeWorkflow) return;

    const resultAction = await dispatch(
      createWorkflowInspection({
        templateId: activeWorkflow.template_id,
        templateName: activeWorkflow.name,
        austinInspectionId: state.austinInspectionId,
        siteCode: state.siteCode,
      })
    );

    // Only start the inspection if the inspection was actually created.
    // createAsyncThunk resolves even on failure (via rejectWithValue), so we
    // must explicitly check the outcome before navigating.
    if (!createWorkflowInspection.fulfilled.match(resultAction)) {
      setStartError(true);
      return;
    }

    // Start the whole-inspection timer now that a brand-new inspection was
    // actually created — this is the "Start Inspection" click completing
    // successfully, the earliest point with a real inspectionId to anchor
    // inspectionTotalMs to. Not called for a resumed draft (handleResumeDraft).
    inspectionFlowTimingLogger.startInspection(resultAction.payload.inspectionId, state.siteCode);

    navigate("/configurationDrivenInspections/execute");
  };

  // Non 404 errors propagate to the caller.
  const getResumableDraftIfExists = async (
    austinInspectionId: string
  ): Promise<InspectionData | null> => {
    setCheckingDraft(true);
    try {
      return await inspectionService.getDraftInspection(austinInspectionId);
    } finally {
      setCheckingDraft(false);
    }
  };

  const handleStartInspection = async () => {
    if (!activeWorkflow) return;

    dispatch(resetInspectionCaptureState());

    if (state.austinInspectionId) {
      try {
        const draft = await getResumableDraftIfExists(state.austinInspectionId);
        if (draft) {
          setDraftInspection(draft);
          setDraftSheetOpen(true);
          return;
        }
      } catch {
        setStartError(true);
        return;
      }
    }

    await startNewInspection();
  };

  const handleResumeDraft = () => {
    setDraftSheetOpen(false);
    if (!draftInspection || !activeWorkflow) return;
    // A 100% complete draft has nothing left to capture — route to review;
    // otherwise route to the resume flow.
    const { complete } = computeResumePosition(activeWorkflow, draftInspection.results);
    const destination = complete ? "review" : "resume";
    navigate(`/configurationDrivenInspections/${draftInspection.inspectionId}/${destination}`);
  };

  const handleClearAndStartNew = async () => {
    setDraftSheetOpen(false);
    await startNewInspection();
  };

  return (
    <div className="austin-details pwa-scrollable">
      <Column spacingInset="500" spacing="400">
        <Row alignmentVertical="center" spacing="300">
          <div onClick={() => navigate(-1)} role="button">
            <Icon tokens={arrowLeftTokens} color="success" />
          </div>
          <Text type="b300" color="primary">
            Inspection Details
          </Text>
        </Row>

        <Column spacing="200">
          <Text type="h500" color="primary">
            {name}
          </Text>
          <Text type="b200" color="secondary">
            FREQUENCY : <b>{frequency}</b>
          </Text>
        </Column>

        {/* DETAILS / HISTORY segmented control */}
        <Row spacing="300" widths="fill">
          <button
            type="button"
            className={`austin-details__tab ${
              activeTab === "DETAILS" ? "austin-details__tab--active" : ""
            }`}
            onClick={() => setActiveTab("DETAILS")}
          >
            DETAILS
          </button>
          <button
            type="button"
            className={`austin-details__tab ${
              activeTab === "HISTORY" ? "austin-details__tab--active" : ""
            }`}
            onClick={() => setActiveTab("HISTORY")}
          >
            HISTORY
          </button>
        </Row>

        {activeTab === "DETAILS" ? (
          <Column spacing="400">
            {/* Summary banner */}
            <div className="austin-details__summary">
              <Row widths={["fit", "fill"]} spacing="500" spacingInset="400 500">
                <Column spacing="none">
                  <Text type="h400" color="inverted">
                    {questionCount}
                  </Text>
                  <Text type="b200" color="inverted">
                    Questions
                  </Text>
                </Column>
              </Row>
            </div>

            {/* Timeline of phases */}
            <div className="austin-details__timeline">
              {phases.map((phase, index) => (
                <div
                  key={`${phase.title}-${index}`}
                  className={`austin-details__phase ${
                    phase.highlighted ? "austin-details__phase--highlight" : ""
                  }`}
                >
                  <div
                    className={`austin-details__card ${
                      phase.highlighted ? "austin-details__card--highlight" : ""
                    }`}
                  >
                    <Column spacing="300">
                      <Row alignmentHorizontal="justify" alignmentVertical="center">
                        <Text
                          type="h300"
                          color={phase.highlighted ? "inverted" : "primary"}
                        >
                          {phase.title}
                        </Text>
                        {phase.mode && (
                          <ThemedTag type="neutral" text={phase.mode} />
                        )}
                      </Row>
                      <Text
                        type="b200"
                        color={phase.highlighted ? "inverted" : "secondary"}
                      >
                        {phase.description}
                      </Text>
                      {phase.meta && (
                        <Text
                          type="b200"
                          color={phase.highlighted ? "inverted" : "primary"}
                        >
                          {phase.meta}
                        </Text>
                      )}
                    </Column>
                  </div>
                </div>
              ))}
            </div>
          </Column>
        ) : (
          <Column spacing="400">
            <Text type="h300" color="primary">
              Inspection History
            </Text>

            {/* Date range filter — defaults to the last 6 months → today. */}
            <DateRangePicker
              value={dateRange}
              onChange={(range: string[]) =>
                setDateRange([range[0] ?? "", range[1] ?? ""])
              }
              startLabel="Start date"
              endLabel="End date"
              monthsInView={1}
              width="100%"
            />

            {historyLoading ? (
              <Column
                className="austin-details__history-empty"
                spacingInset="600"
                alignmentHorizontal="center"
              >
                <Loader type="circular" size="medium" />
              </Column>
            ) : historyError ? (
              <Column
                className="austin-details__history-empty"
                spacingInset="600"
                spacing="300"
                alignmentHorizontal="center"
              >
                <Text color="secondary">Couldn't load inspection history.</Text>
                <Button type="link" onClick={fetchHistory}>
                  Try Again
                </Button>
              </Column>
            ) : history.length === 0 ? (
              <Column
                className="austin-details__history-empty"
                spacingInset="600"
                alignmentHorizontal="center"
              >
                <Text color="secondary">No completed inspections yet.</Text>
              </Column>
            ) : (
              <Column spacing="300">
                {history.map((inspection) => (
                  <HistoryInspectionCard
                    key={inspection.inspectionId}
                    inspection={inspection}
                    onClick={handleHistoryCardClick}
                  />
                ))}
              </Column>
            )}
          </Column>
        )}
      </Column>

      {/* Spacer so the last card is not hidden behind the fixed footer. */}
      <Row minHeight="80px" />

      <Row className="austin-details__footer" spacingInset="400" width="100%" widths="fill">
        <Button
          minWidth="100%"
          size="large"
          className="austin-details__start-button"
          onClick={handleStartInspection}
          disabled={scheduledWorkflowLoading || checkingDraft || !activeWorkflow}
        >
          {scheduledWorkflowLoading || checkingDraft
            ? "LOADING…"
            : "START INSPECTION"}
        </Button>
      </Row>

      <ErrorSheet
        open={startError}
        title="Couldn't start inspection"
        message="Something went wrong. Please try again."
        onClose={() => setStartError(false)}
        actions={[
          {
            label: "Try Again",
            onClick: () => {
              setStartError(false);
              handleStartInspection();
            },
          },
        ]}
      />

      <ErrorSheet
        open={scheduledWorkflowError}
        title="Couldn't load inspection"
        message="We couldn't load this inspection's details. Please try again."
        onClose={() => navigate(-1)}
        dismissLabel="Go Back"
        actions={[
          {
            label: "Retry",
            onClick: fetchScheduledWorkflow,
          },
        ]}
      />

      <DraftInspectionSheet
        open={draftSheetOpen}
        expiresAt={draftInspection?.expiresAt}
        onResume={handleResumeDraft}
        onClearAndStartNew={handleClearAndStartNew}
        onClose={() => setDraftSheetOpen(false)}
      />
    </div>
  );
};

export default AustinInspectionCheckpointsPage;
